import React, { useState } from "react";
import MainLayout from "../components/layout/MainLayout";
import CategoryList from "../features/categories/CategoryList";
import CategoryForm from "../features/categories/CategoryForm";
import { useNotification } from "../context/NotificationContext";
import categoryService from "../services/categoryService";

const Categories = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [viewingCategory, setViewingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { showSuccess } = useNotification();

  const handleCreate = () => {
    setEditingCategory(null);
    setShowForm(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleView = (category) => {
    setViewingCategory(category);
  };

  const handleCloseView = () => {
    setViewingCategory(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      let response;

      if (editingCategory) {
        response = await categoryService.updateCategory(
          editingCategory.id,
          formData
        );
        if (response.success) {
          showSuccess("Category updated successfully!");
        }
      } else {
        response = await categoryService.createCategory(formData);
        if (response.success) {
          showSuccess("Category created successfully!");
        }
      }

      setShowForm(false);
      setEditingCategory(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <MainLayout>
      <div className="categories-page">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">Category Management</h1>
            <p className="text-muted mb-0">Manage event and club categories</p>
          </div>
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="fas fa-plus me-2"></i>
            Add Category
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i
                      className={`fas ${
                        editingCategory ? "fa-edit" : "fa-plus"
                      } me-2`}
                    ></i>
                    {editingCategory ? "Edit Category" : "Add New Category"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleFormCancel}
                    disabled={loading}
                  ></button>
                </div>
                <div className="modal-body">
                  <CategoryForm
                    category={editingCategory}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormCancel}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {viewingCategory && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-tags me-2"></i>
                    Category Details
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseView}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-8">
                      {/* Category Header */}
                      <div className="d-flex align-items-center mb-4">
                        <div
                          className="category-icon bg-success rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{ width: "60px", height: "60px" }}
                        >
                          <i className="fas fa-tags text-white fs-4"></i>
                        </div>
                        <div>
                          <h4 className="fw-bold text-dark mb-0">
                            {viewingCategory.name}
                          </h4>
                          {viewingCategory.parentCategoryName && (
                            <p className="text-muted mb-1">
                              <i className="fas fa-sitemap me-1"></i>
                              Parent: {viewingCategory.parentCategoryName}
                            </p>
                          )}
                          <span className="badge bg-success">
                            <i className="fas fa-layer-group me-1"></i>
                            {viewingCategory.parentCategoryId
                              ? "Sub-Category"
                              : "Main Category"}
                          </span>
                        </div>
                      </div>

                      {/* Basic Information */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            CATEGORY ID
                          </label>
                          <p className="text-dark fs-5">{viewingCategory.id}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            TYPE
                          </label>
                          <p className="text-dark fs-5">
                            {viewingCategory.parentCategoryId
                              ? "Sub-Category"
                              : "Main Category"}
                          </p>
                        </div>
                      </div>

                      {/* Parent Category */}
                      {viewingCategory.parentCategoryName && (
                        <div className="row mb-4">
                          <div className="col-md-12">
                            <label className="form-label fw-bold text-muted small">
                              PARENT CATEGORY
                            </label>
                            <p className="text-dark">
                              {viewingCategory.parentCategoryName}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Timestamps */}
                      <div className="row">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            CREATED
                          </label>
                          <p className="text-dark">
                            {formatDate(viewingCategory.createdAt)}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            LAST UPDATED
                          </label>
                          <p className="text-dark">
                            {formatDate(viewingCategory.updatedAt)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Statistics Panel */}
                    <div className="col-md-4">
                      <div className="card border-0 bg-light h-100">
                        <div className="card-body">
                          <h6 className="fw-bold mb-4">Category Statistics</h6>

                          {/* Events */}
                          <div className="d-flex align-items-center mb-3">
                            <div
                              className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                              style={{ width: "40px", height: "40px" }}
                            >
                              <i className="fas fa-calendar-alt text-white"></i>
                            </div>
                            <div>
                              <div className="fw-bold fs-4">
                                {viewingCategory.totalEvents || 0}
                              </div>
                              <small className="text-muted">Events</small>
                            </div>
                          </div>

                          {/* Clubs */}
                          <div className="d-flex align-items-center mb-4">
                            <div
                              className="bg-info rounded-circle d-flex align-items-center justify-content-center me-3"
                              style={{ width: "40px", height: "40px" }}
                            >
                              <i className="fas fa-users text-white"></i>
                            </div>
                            <div>
                              <div className="fw-bold fs-4">
                                {viewingCategory.totalClubs || 0}
                              </div>
                              <small className="text-muted">Clubs</small>
                            </div>
                          </div>

                          {/* Category Type Info */}
                          <div className="border-top pt-3">
                            <h6 className="fw-bold mb-3">Category Info</h6>
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-layer-group text-success me-2"></i>
                              <span className="small">
                                {viewingCategory.parentCategoryId
                                  ? "Sub-Category"
                                  : "Main Category"}
                              </span>
                            </div>
                            {viewingCategory.parentCategoryName && (
                              <div className="d-flex align-items-center">
                                <i className="fas fa-sitemap text-info me-2"></i>
                                <span className="small">
                                  Under: {viewingCategory.parentCategoryName}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      handleCloseView();
                      handleEdit(viewingCategory);
                    }}
                  >
                    <i className="fas fa-edit me-2"></i>
                    Edit Category
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseView}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Categories List */}
        <CategoryList
          onEdit={handleEdit}
          onView={handleView}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </MainLayout>
  );
};

export default Categories;
